<script setup>
/**
 * Aceleres: escolha do período de fixação, como no WhatsApp.
 *
 * Quem fixa uma mensagem chama `prompt()` e recebe uma Promise que resolve com
 * a duração escolhida ('24h' | '7d' | '30d') ou `null` se o agente cancelar.
 * Manter a escolha numa Promise é o que deixa o chamador escrever o fluxo
 * inteiro numa linha só — sem isso cada ponto de fixação (menu de contexto,
 * galeria, visualizador de PDF) teria de guardar o próprio estado de "modal
 * aberto esperando por qual mensagem".
 *
 * Desafixar não passa por aqui: o endpoint alterna e não há prazo a escolher.
 */
import { onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  PIN_DURATIONS,
  DEFAULT_PIN_DURATION,
} from 'dashboard/helper/pinnedMessages';

const { t } = useI18n();

const dialogRef = ref(null);
const selectedDuration = ref(DEFAULT_PIN_DURATION);

// A Promise aberta pelo `prompt()`. Fica aqui porque tanto o confirmar quanto
// o fechar (X, Esc, clique fora) precisam resolvê-la — se só o botão resolvesse,
// um Esc deixaria o chamador esperando para sempre.
const pending = ref(null);

const settle = duration => {
  pending.value?.(duration);
  pending.value = null;
};

const prompt = () => {
  selectedDuration.value = DEFAULT_PIN_DURATION;
  dialogRef.value?.open();

  return new Promise(resolve => {
    pending.value = resolve;
  });
};

// A ordem aqui é o que faz o botão Fixar funcionar: `close()` do Dialog emite
// `close` de forma SÍNCRONA, e o nosso `handleClose` resolve a Promise com
// `null`. Fechar antes de resolver entregava sempre `null` a quem esperava, e
// quem fixa lê isso como "o agente cancelou" — o pedido de fixação nunca saía.
// Resolver primeiro deixa `pending` zerada, e aí o `close` que vem a seguir não
// tem mais nada para cancelar.
const handleConfirm = () => {
  settle(selectedDuration.value);
  dialogRef.value?.close();
};

// `close` do Dialog cobre o botão Cancelar, o Esc e o clique fora. Também
// dispara depois do `handleConfirm`, mas aí `pending` já foi resolvida e
// zerada, então cancelar em cima do próprio confirmar não acontece.
const handleClose = () => settle(null);

// O modal vive dentro de quem fixa (menu de contexto, galeria, visualizador).
// Se esse dono sumir com a escolha em aberto, quem esperava a Promise ficaria
// parado para sempre — desaparecer conta como cancelar.
onBeforeUnmount(() => settle(null));

defineExpose({ prompt });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    mobile-inset
    :title="t('CONVERSATION.PIN_DURATION.TITLE')"
    :description="t('CONVERSATION.PIN_DURATION.DESCRIPTION')"
    @confirm="handleConfirm"
    @close="handleClose"
  >
    <div class="flex flex-col gap-1">
      <label
        v-for="option in PIN_DURATIONS"
        :key="option.value"
        class="flex items-center gap-3 py-2 cursor-pointer select-none"
      >
        <input
          v-model="selectedDuration"
          type="radio"
          name="aceleres-pin-duration"
          :value="option.value"
          class="w-5 h-5 accent-n-teal-10 cursor-pointer"
        />
        <span class="text-sm text-n-slate-12">{{ t(option.labelKey) }}</span>
      </label>
    </div>
    <!--
      Rodapé próprio: o padrão do Dialog são dois botões de largura inteira, e
      o WhatsApp encosta os dois à direita — "Cancelar" sem preenchimento e
      "Fixar" numa pílula escura. O `type="submit"` mantém o Enter funcionando,
      porque quem confirma o <form> do Dialog é ele.
    -->
    <template #footer>
      <div class="flex items-center justify-end w-full gap-2">
        <Button
          variant="ghost"
          color="teal"
          type="button"
          class="!rounded-full"
          :label="t('CONVERSATION.PIN_DURATION.CANCEL')"
          @click="dialogRef?.close()"
        />
        <Button
          variant="solid"
          color="teal"
          type="submit"
          class="!rounded-full px-6"
          :label="t('CONVERSATION.PIN_DURATION.CONFIRM')"
        />
      </div>
    </template>
  </Dialog>
</template>
